// apps/web/src/hooks/useFeatureFlag.ts

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * Feature flag configuration
 *
 * Structure:
 * {
 *   flagKey: {
 *     enabled: boolean,      // Base on/off state
 *     variant?: string,      // A/B test variant ('A', 'B', 'control', 'treatment')
 *     rollout?: number,      // Percentage rollout (0-100)
 *   }
 * }
 */

interface FeatureFlagConfig {
  enabled: boolean;
  variant?: string;
  rollout?: number;
}

type FeatureFlags = Record<string, FeatureFlagConfig>;

// Default feature flags configuration
const DEFAULT_FLAGS: FeatureFlags = {
  'hierarchical-entitlements': {
    enabled: false, // Controlled by backend seed — keep false until migration validates
  },
};

const STORAGE_KEY = 'lov_feature_flags';
const USER_VARIANT_KEY = 'lov_user_variant';

/**
 * Get persisted user variant assignment
 * Ensures consistent A/B test experience across sessions
 */
function getUserVariant(): string {
  if (typeof window === 'undefined') return 'control';

  let variant = localStorage.getItem(USER_VARIANT_KEY);

  if (!variant) {
    // Assign user to variant based on deterministic hash
    // In production, you'd use userId hash for logged-in users
    const randomValue = Math.random() * 100;
    variant = randomValue < 50 ? 'A' : 'B';
    localStorage.setItem(USER_VARIANT_KEY, variant);
  }

  return variant;
}

/**
 * Get feature flags from localStorage or defaults
 */
function getFeatureFlags(): FeatureFlags {
  if (typeof window === 'undefined') return DEFAULT_FLAGS;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_FLAGS, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.error('Failed to load feature flags:', error);
  }

  return DEFAULT_FLAGS;
}

/**
 * Save feature flags to localStorage
 */
function saveFeatureFlags(flags: FeatureFlags): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
  } catch (error) {
    console.error('Failed to save feature flags:', error);
  }
}

/**
 * Check if a feature is enabled for the current user
 * Considers: base enabled state, variant assignment, and rollout percentage
 */
function isFeatureEnabled(flagKey: string, flags: FeatureFlags = getFeatureFlags()): boolean {
  const flag = flags[flagKey];

  if (!flag) {
    // Flag not configured, default to disabled
    return false;
  }

  if (!flag.enabled) {
    return false;
  }

  // Check rollout percentage
  if (typeof flag.rollout === 'number') {
    const userVariant = getUserVariant();
    // Simple hash-based assignment (in production, use userId)
    const hash = userVariant.charCodeAt(0) % 100;
    return hash < flag.rollout;
  }

  return true;
}

/**
 * Get the variant for a feature flag
 */
function getFeatureVariant(
  flagKey: string,
  flags: FeatureFlags = getFeatureFlags()
): string | undefined {
  const flag = flags[flagKey];

  if (!flag || !flag.enabled) {
    return 'control';
  }

  if (flag.variant) {
    // Check if user should receive this variant based on rollout
    if (typeof flag.rollout === 'number') {
      const userVariant = getUserVariant();
      const hash = userVariant.charCodeAt(0) % 100;

      if (hash < flag.rollout) {
        return flag.variant;
      }
      return 'control';
    }

    return flag.variant;
  }

  return undefined;
}

/**
 * React hook for feature flags
 *
 * Usage:
 * const { isEnabled, variant } = useFeatureFlag('dark-mode');
 *
 * if (isEnabled) {
 *   // Show dark mode feature
 * }
 *
 * if (variant === 'B') {
 *   // Show variant B
 * }
 */
export function useFeatureFlag(flagKey: string) {
  const [flags, setFlags] = useState<FeatureFlags>(getFeatureFlags);

  // Fetch backend flags (background, non-blocking)
  const { data: backendFlagMap } = useQuery({
    queryKey: ['feature-flags', 'client'],
    queryFn: async (): Promise<Record<string, boolean>> => {
      return api.get<Record<string, boolean>>('/feature-flags/client-flags');
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Merge when backend flags arrive: DEFAULT_FLAGS ← backend ← localStorage overrides
  useEffect(() => {
    if (backendFlagMap) {
      const localOverrides = (() => {
        try {
          return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') as FeatureFlags;
        } catch {
          return {} as FeatureFlags;
        }
      })();

      const merged: FeatureFlags = { ...DEFAULT_FLAGS };
      for (const [key, enabled] of Object.entries(backendFlagMap)) {
        merged[key] = { ...(merged[key] ?? {}), enabled };
      }
      setFlags({ ...merged, ...localOverrides });
    }
  }, [backendFlagMap]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try {
          const localOverrides = JSON.parse(e.newValue) as FeatureFlags;
          // Re-merge with backend flags if available
          if (backendFlagMap) {
            const merged: FeatureFlags = { ...DEFAULT_FLAGS };
            for (const [key, enabled] of Object.entries(backendFlagMap)) {
              merged[key] = { ...(merged[key] ?? {}), enabled };
            }
            setFlags({ ...merged, ...localOverrides });
          } else {
            setFlags({ ...DEFAULT_FLAGS, ...localOverrides });
          }
        } catch (error) {
          console.error('Failed to parse feature flags from storage event:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [flagKey, backendFlagMap]);

  const toggle = () => {
    const newFlags = { ...flags };
    if (!newFlags[flagKey]) {
      newFlags[flagKey] = { enabled: true };
    } else {
      newFlags[flagKey] = { ...newFlags[flagKey], enabled: !newFlags[flagKey].enabled };
    }
    setFlags(newFlags);
    saveFeatureFlags(newFlags);
  };

  return {
    isEnabled: isFeatureEnabled(flagKey, flags),
    variant: getFeatureVariant(flagKey, flags),
    toggle,
  };
}
